import React, { useEffect, useState } from 'react'
import { cn } from '../../utils/cn'

export interface TableColumn<T = any> {
  key: string
  header: string
  accessor: keyof T | ((item: T) => React.ReactNode)
  sortable?: boolean
  className?: string
  cellClassName?: string
  render?: (value: any, item: T) => React.ReactNode
  /**
   * Darstellung in der Kartenliste (< 768 px):
   * 'title' = Hauptzeile der Karte, 'subtitle' = Zeile darunter, 'badge' = Status-Badge (oben/unter dem Titel),
   * 'meta' = Label/Wert-Feld, 'actions' = Zeilenaktionen (unten, mind. 48 px), 'hidden' = nicht anzeigen.
   * Standard: erste Spalte = title, key 'actions' = actions, key/Kopf mit status|zustand|aktiv|priorit = badge,
   * alle übrigen = meta (höchstens 4 automatisch, weitere ausgeblendet).
   */
  mobile?: 'title' | 'subtitle' | 'badge' | 'meta' | 'actions' | 'hidden'
}

interface DataTableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  keyAccessor: (item: T) => string
  sort?: { key: string; direction: 'asc' | 'desc' }
  onSort?: (key: string) => void
  onRowClick?: (item: T) => void
  loading?: boolean
  emptyMessage?: string
  className?: string
  rowClassName?: (item: T) => string
  /** Kartenliste unter 768 px (Standard: an). `false` behält die Tabelle mit Querscroll. */
  mobileCards?: boolean
}

const Q_CARDS = '(max-width: 767.98px)'

function useCardMode(enabled: boolean) {
  const [on, setOn] = useState(() => enabled && typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(Q_CARDS).matches)
  useEffect(() => {
    if (!enabled || !window.matchMedia) { setOn(false); return }
    const mq = window.matchMedia(Q_CARDS)
    const f = () => setOn(mq.matches)
    f()
    mq.addEventListener('change', f)
    return () => mq.removeEventListener('change', f)
  }, [enabled])
  return on
}

const INTERAKTIV = 'button,a,input,select,textarea,label,summary,[role="button"],[data-no-row-click]'

/** Zeilenklick nur auslösen, wenn nicht auf ein interaktives Element geklickt und kein Text markiert wurde. */
function zeilenKlick<T>(item: T, onRowClick?: (item: T) => void) {
  return (e: React.MouseEvent<HTMLElement>) => {
    if (!onRowClick) return
    const ziel = e.target as HTMLElement
    const naechstes = ziel.closest?.(INTERAKTIV)
    if (naechstes && naechstes !== e.currentTarget && e.currentTarget.contains(naechstes)) return
    if (window.getSelection()?.toString()) return
    onRowClick(item)
  }
}

function zeilenTaste<T>(item: T, onRowClick?: (item: T) => void) {
  return (e: React.KeyboardEvent<HTMLElement>) => {
    if (!onRowClick || e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(item) }
  }
}

const BADGE_RE = /status|zustand|aktiv|priorit/i
const MAX_AUTO_META = 4

type Rolle = NonNullable<TableColumn['mobile']>

function rollen(columns: TableColumn[]): Rolle[] {
  let autoMeta = 0
  const explicitMeta = columns.filter(c => c.mobile === 'meta').length
  const hatTitel = columns.some(c => c.mobile === 'title')
  return columns.map((c, i) => {
    if (c.mobile) return c.mobile
    if (c.key === 'actions' || /^aktionen$/i.test(c.header)) return 'actions'
    if (!hatTitel && i === 0) return 'title'
    if (BADGE_RE.test(c.key) || BADGE_RE.test(c.header)) return 'badge'
    if (!c.header.trim()) return 'hidden'
    autoMeta++
    return autoMeta + explicitMeta <= MAX_AUTO_META ? 'meta' : 'hidden'
  })
}

export function DataTable<T>({
  data,
  columns,
  keyAccessor,
  sort,
  onSort,
  onRowClick,
  loading = false,
  emptyMessage = 'Keine Daten vorhanden',
  className,
  rowClassName,
  mobileCards = true
}: DataTableProps<T>) {
  const cards = useCardMode(mobileCards)
  const cell = (col: TableColumn<T>, item: T): React.ReactNode =>
    col.render
      ? col.render(item[col.accessor as keyof T], item)
      : typeof col.accessor === 'function'
        ? col.accessor(item)
        : (item[col.accessor] as React.ReactNode) ?? '-'

  if (cards && loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-dark-bg-tertiary" />)}
      </div>
    )
  }

  if (cards && data.length > 0) {
    const rs = rollen(columns as TableColumn[])
    const pick = (r: Rolle) => columns.filter((_, i) => rs[i] === r)
    const titel = pick('title'), unter = pick('subtitle'), badges = pick('badge'), metas = pick('meta'), aktionen = pick('actions')
    return (
      <ul className={cn('space-y-3', className)} data-ui-cards="">
        {data.map(item => (
          <li
            key={keyAccessor(item)}
            className={cn(
              'rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4',
              onRowClick && 'cursor-pointer active:bg-dark-bg-tertiary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
              rowClassName?.(item)
            )}
            onClick={onRowClick ? zeilenKlick(item, onRowClick) : undefined}
            onKeyDown={onRowClick ? zeilenTaste(item, onRowClick) : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            title={onRowClick ? 'Antippen zum Anzeigen' : undefined}
          >
            {(titel.length > 0 || unter.length > 0) && (
              <div className="min-w-0">
                {titel.map(c => <div key={c.key} className="break-words text-base font-semibold text-dark-text">{cell(c, item)}</div>)}
                {unter.map(c => <div key={c.key} className="mt-0.5 break-words text-sm text-dark-text-muted">{cell(c, item)}</div>)}
              </div>
            )}
            {badges.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {badges.map(c => <div key={c.key}>{cell(c, item)}</div>)}
              </div>
            )}
            {metas.length > 0 && (
              <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                {metas.map(c => (
                  <React.Fragment key={c.key}>
                    <dt className="text-dark-text-subtle">{c.header}</dt>
                    <dd className="min-w-0 break-words text-dark-text">{cell(c, item)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
            {aktionen.length > 0 && (
              <div
                className="mt-3 flex flex-wrap items-center justify-end gap-3 border-t border-dark-border pt-3 [&_button]:!h-12 [&_button]:!min-w-12 [&_a]:!min-h-12"
                onClick={e => e.stopPropagation()}
              >
                {aktionen.map(c => <div key={c.key}>{cell(c, item)}</div>)}
              </div>
            )}
          </li>
        ))}
      </ul>
    )
  }

  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-border">
              {columns.map(col => (
                <th key={col.key} className="ui-th">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="animate-pulse">
                {columns.map(col => (
                  <td key={col.key} className="ui-td">
                    <div className="h-4 bg-dark-bg-tertiary rounded w-3/4" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (data.length === 0) {
    return <div className="text-center py-12"><p className="text-dark-text-muted">{emptyMessage}</p></div>
  }

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-dark-border">
            {columns.map(col => (
              <th
                key={col.key}
                className={cn(
                  'ui-th',
                  col.sortable && 'cursor-pointer hover:bg-dark-bg-tertiary/50'
                )}
                onClick={col.sortable && onSort ? () => onSort(col.key as string) : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-dark-border">
          {data.map(item => (
            <tr
              key={keyAccessor(item)}
              className={cn('transition-colors', onRowClick && 'cursor-pointer hover:bg-dark-bg-tertiary/50 focus:outline-none focus-visible:bg-dark-bg-tertiary/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400', rowClassName?.(item))}
              onClick={onRowClick ? zeilenKlick(item, onRowClick) : undefined}
              onKeyDown={onRowClick ? zeilenTaste(item, onRowClick) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              title={onRowClick ? 'Klicken zum Anzeigen' : undefined}
            >
              {columns.map(col => (
                <td key={col.key} className={cn('ui-td', col.cellClassName)}>
                  {col.render
                    ? col.render(item[col.accessor as keyof T], item)
                    : typeof col.accessor === 'function'
                      ? col.accessor(item)
                      : (item[col.accessor] as React.ReactNode) ?? '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}